import React, { useState } from 'react';
import { Box, Typography, Button, Drawer, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronDown, ChevronRight, Lightbulb, ArrowRight, Menu } from 'lucide-react';

const PRIMARY = '#0d9488';
const PRIMARY_HOVER = '#0f766e';
const PRIMARY_LIGHT = '#CCFBF1';
const BG = '#F8FAFC';
const CARD_BG = '#FFFFFF';
const TEXT_DARK = '#1E293B';
const TEXT_MUTED = '#64748B';
const BORDER = '#E5E7EB';
const MENU_TITLE_FONT = '"Poppins", "Inter", sans-serif';
const MENU_SECTION_FONT = '"Poppins", "Inter", sans-serif';
const SUBMENU_FONT = '"Inter", "Segoe UI", sans-serif';

type MenuSection = {
  id: string;
  label: string;
  children: { id: string; label: string }[];
};

const SIDEBAR_SECTIONS: MenuSection[] = [
  {
    id: 'intro-robotics',
    label: 'Introduction to Robotics',
    children: [
      { id: 'what-is-robotics', label: 'What is Robotics?' },
      { id: 'applications-robotics', label: 'Applications of Robotics' },
      { id: 'components-of-robot', label: 'Components of a Robot' },
      { id: 'types-of-robots', label: 'Types of Robots (Industrial, Service, Educational)' },
    ],
  },
  {
    id: 'electronics',
    label: 'Electronics Fundamentals',
    children: [
      { id: 'voltage-current-resistance', label: 'Voltage, Current, Resistance' },
      { id: 'breadboard-basics', label: 'Breadboard Basics' },
      { id: 'power-battery-basics', label: 'Power Supply & Battery Basics' },
      { id: 'basic-components', label: 'Basic Electronic Components (LED, Resistor, Capacitor)' },
    ],
  },
  {
    id: 'microcontrollers',
    label: 'Microcontrollers & Embedded Platforms',
    children: [
      { id: 'what-is-microcontroller', label: 'What is a Microcontroller?' },
      { id: 'avr-microcontroller', label: 'AVR Microcontroller' },
      { id: 'arduino-platform', label: 'Arduino Platform' },
      { id: 'raspberry-pi', label: 'Raspberry Pi (Single Board Computer)' },
      { id: 'mcu-vs-microprocessor', label: 'Microcontroller vs Microprocessor' },
    ],
  },
  {
    id: 'sensors',
    label: 'Sensors & Input Devices',
    children: [
      { id: 'what-is-sensor', label: 'What is a Sensor?' },
      { id: 'ir-sensor', label: 'IR Sensor' },
      { id: 'pir-motion-sensor', label: 'PIR Motion Sensor' },
      { id: 'moisture-sensor', label: 'Moisture Sensor' },
      { id: 'sound-sensor', label: 'Sound Sensor' },
      { id: 'flame-sensor', label: 'Flame Sensor' },
      { id: 'sensor-interfacing', label: 'Sensor Interfacing Basics' },
    ],
  },
  {
    id: 'output-actuators',
    label: 'Output Devices & Actuators',
    children: [
      { id: 'what-is-actuator', label: 'What is an Actuator?' },
      { id: 'dc-motor', label: 'DC Motor' },
      { id: 'stepper-motor', label: 'Stepper Motor' },
      { id: 'servo-motor', label: 'Servo Motor' },
      { id: 'led-display', label: 'LED & Display Modules' },
      { id: 'relay-modules', label: 'Relay Modules' },
    ],
  },
  {
    id: 'robot-control-programming',
    label: 'Robot Control & Programming',
    children: [
      { id: 'arduino-programming-basics', label: 'Arduino Programming Basics' },
      { id: 'reading-sensor-data', label: 'Reading Sensor Data' },
      { id: 'motor-control-programming', label: 'Motor Control Programming' },
      { id: 'serial-communication', label: 'Serial Communication' },
      { id: 'basic-automation-logic', label: 'Basic Automation Logic' },
    ],
  },
  {
    id: 'robotics-communication',
    label: 'Robotics Communication',
    children: [
      { id: 'bluetooth-communication', label: 'Bluetooth Communication' },
      { id: 'wifi-communication', label: 'WiFi Communication' },
      { id: 'rf-communication-basics', label: 'RF Communication Basics' },
      { id: 'iot-basics-robotics', label: 'IoT Basics for Robotics' },
    ],
  },
  {
    id: 'robotics-projects',
    label: 'Robotics Projects',
    children: [
      { id: 'line-follower-robot', label: 'Line Follower Robot' },
      { id: 'obstacle-avoidance-robot', label: 'Obstacle Avoidance Robot' },
      { id: 'smart-irrigation-system', label: 'Smart Irrigation System' },
      { id: 'home-automation-robot', label: 'Home Automation Robot' },
      { id: 'voice-controlled-robot', label: 'Voice-Controlled Robot' },
    ],
  },
  {
    id: 'career-paths',
    label: 'Robotics Career Paths',
    children: [
      { id: 'robotics-engineer', label: 'Robotics Engineer' },
      { id: 'embedded-systems-engineer', label: 'Embedded Systems Engineer' },
      { id: 'iot-engineer', label: 'IoT Engineer' },
      { id: 'automation-engineer', label: 'Automation Engineer' },
      { id: 'drone-robotics-developer', label: 'Drone & Robotics Developer' },
    ],
  },
];

type SectionLandingContent = {
  title: string;
  intro: string;
  whatYouWillLearn: string[];
  whyItMatters: string;
  actionPrompt: string;
  startWithTopicId: string | null;
  startWithLabel: string;
  tip: string;
};

const SECTION_LANDING: Record<string, SectionLandingContent> = {
  'intro-robotics': {
    title: 'Introduction to Robotics',
    intro:
      'Start with the big picture: what robotics is, where robots are used in the real world, the main parts of a robot, and the different types—industrial, service, and educational.',
    whatYouWillLearn: [
      'What is Robotics?',
      'Applications of Robotics',
      'Components of a Robot',
      'Types of Robots (Industrial, Service, Educational)',
    ],
    whyItMatters:
      'A clear foundation helps you choose the right platform and project as you move into electronics and programming.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'what-is-robotics',
    startWithLabel: 'What is Robotics?',
    tip: 'Complete this section first to see how theory connects to the components and projects you will build.',
  },
  electronics: {
    title: 'Electronics Fundamentals',
    intro:
      'Learn the basics of electricity and circuits: voltage, current, resistance, how to use a breadboard, power and batteries, and common components like LEDs, resistors, and capacitors.',
    whatYouWillLearn: [
      'Voltage, Current, Resistance',
      'Breadboard Basics',
      'Power Supply & Battery Basics',
      'Basic Electronic Components (LED, Resistor, Capacitor)',
    ],
    whyItMatters:
      'Every robot is built on circuits; these fundamentals are needed to read schematics and wire sensors and motors safely.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'voltage-current-resistance',
    startWithLabel: 'Voltage, Current, Resistance',
    tip: 'Always double-check polarity and current limits before connecting components to avoid damage.',
  },
  microcontrollers: {
    title: 'Microcontrollers & Embedded Platforms',
    intro:
      'Understand the brain of a robot: what a microcontroller is, AVR and Arduino, Raspberry Pi as a single-board computer, and how microcontrollers differ from microprocessors.',
    whatYouWillLearn: [
      'What is a Microcontroller?',
      'AVR Microcontroller',
      'Arduino Platform',
      'Raspberry Pi (Single Board Computer)',
      'Microcontroller vs Microprocessor',
    ],
    whyItMatters:
      'Choosing the right platform (Arduino for real-time control, Raspberry Pi for more computing) shapes your project design.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'what-is-microcontroller',
    startWithLabel: 'What is a Microcontroller?',
    tip: 'Start with Arduino for quick prototyping; move to Raspberry Pi when you need networking or heavy processing.',
  },
  sensors: {
    title: 'Sensors & Input Devices',
    intro:
      'Explore how robots sense the world: what a sensor is, common sensors (IR, PIR motion, moisture, sound, flame), and the basics of connecting sensors to your microcontroller.',
    whatYouWillLearn: [
      'What is a Sensor?',
      'IR Sensor',
      'PIR Motion Sensor',
      'Moisture Sensor',
      'Sound Sensor',
      'Flame Sensor',
      'Sensor Interfacing Basics',
    ],
    whyItMatters:
      'Sensors are the inputs that let your robot react to its environment—essential for line followers, obstacle avoidance, and smart systems.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'what-is-sensor',
    startWithLabel: 'What is a Sensor?',
    tip: 'Always verify sensor output with Serial.print or a simple LED before building logic around it.',
  },
  'output-actuators': {
    title: 'Output Devices & Actuators',
    intro:
      'Learn how robots act on the world: what an actuator is, DC motors, stepper and servo motors, LEDs and displays, and relay modules for switching higher power.',
    whatYouWillLearn: [
      'What is an Actuator?',
      'DC Motor',
      'Stepper Motor',
      'Servo Motor',
      'LED & Display Modules',
      'Relay Modules',
    ],
    whyItMatters:
      'Motors and relays drive movement and control; choosing the right actuator (DC vs servo vs stepper) depends on precision and load.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'what-is-actuator',
    startWithLabel: 'What is an Actuator?',
    tip: 'Use motor driver modules (e.g. L298N) to interface motors—never drive them directly from the microcontroller.',
  },
  'robot-control-programming': {
    title: 'Robot Control & Programming',
    intro:
      'Program your robot: Arduino basics, reading sensor data, controlling motors, serial communication for debugging and PC link, and simple automation logic.',
    whatYouWillLearn: [
      'Arduino Programming Basics',
      'Reading Sensor Data',
      'Motor Control Programming',
      'Serial Communication',
      'Basic Automation Logic',
    ],
    whyItMatters:
      'This is where sensors and actuators come together; good code structure makes debugging and adding features much easier.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'arduino-programming-basics',
    startWithLabel: 'Arduino Programming Basics',
    tip: 'Test each block (sensor read, motor drive) separately before combining them into a full behavior.',
  },
  'robotics-communication': {
    title: 'Robotics Communication',
    intro:
      'Connect your robot wirelessly: Bluetooth and WiFi for remote control and data, RF basics for simple wireless links, and IoT concepts for cloud and app integration.',
    whatYouWillLearn: [
      'Bluetooth Communication',
      'WiFi Communication',
      'RF Communication Basics',
      'IoT Basics for Robotics',
    ],
    whyItMatters:
      'Wireless communication enables remote control, telemetry, and smart home or industry integration.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'bluetooth-communication',
    startWithLabel: 'Bluetooth Communication',
    tip: 'Bluetooth is great for short-range phone/PC control; WiFi is better when you need internet or longer range.',
  },
  'robotics-projects': {
    title: 'Robotics Projects',
    intro:
      'Apply everything you have learned: build a line follower, obstacle avoidance robot, smart irrigation system, home automation robot, and voice-controlled robot.',
    whatYouWillLearn: [
      'Line Follower Robot',
      'Obstacle Avoidance Robot',
      'Smart Irrigation System',
      'Home Automation Robot',
      'Voice-Controlled Robot',
    ],
    whyItMatters:
      'Projects tie theory to practice and give you a portfolio of working robots for learning and career growth.',
    actionPrompt: 'Select a topic from the left menu to start learning.',
    startWithTopicId: 'line-follower-robot',
    startWithLabel: 'Line Follower Robot',
    tip: 'Start with a line follower or obstacle avoider—they use the same parts and logic you have already learned.',
  },
  'career-paths': {
    title: 'Robotics Career Paths',
    intro:
      'Explore roles where robotics skills apply: Robotics Engineer, Embedded Systems Engineer, IoT Engineer, Automation Engineer, and Drone & Robotics Developer.',
    whatYouWillLearn: [
      'Robotics Engineer',
      'Embedded Systems Engineer',
      'IoT Engineer',
      'Automation Engineer',
      'Drone & Robotics Developer',
    ],
    whyItMatters:
      'Knowing these paths helps you focus your learning and choose projects that align with your goals.',
    actionPrompt: 'Select a role from the left menu to explore.',
    startWithTopicId: 'robotics-engineer',
    startWithLabel: 'Robotics Engineer',
    tip: 'Skills from one path (e.g. embedded C, sensors) often transfer to others like IoT and automation.',
  },
};

function SectionLandingView({
  content,
  onStartWithTopic,
  progress,
  activeId,
}: {
  content: SectionLandingContent;
  onStartWithTopic: (topicId: string) => void;
  progress: number;
  activeId: string | null;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Typography
        component="h1"
        sx={{
          fontFamily: MENU_SECTION_FONT,
          fontSize: { xs: '1.75rem', md: '2rem' },
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 2,
        }}
      >
        {content.title}
      </Typography>

      <Typography
        sx={{
          color: TEXT_MUTED,
          lineHeight: 1.8,
          mb: 3,
          fontSize: '1rem',
        }}
      >
        {content.intro}
      </Typography>

      <Typography
        component="h2"
        sx={{
          fontSize: '1.2rem',
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 1.5,
        }}
      >
        What You Will Learn
      </Typography>
      <Box component="ul" sx={{ pl: 2.5, mb: 3, '& li': { color: TEXT_MUTED, lineHeight: 1.9 } }}>
        {content.whatYouWillLearn.map((item, i) => (
          <motion.li
            key={item}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.05 * i }}
          >
            {item}
          </motion.li>
        ))}
      </Box>

      <Typography
        component="h2"
        sx={{
          fontSize: '1.2rem',
          fontWeight: 700,
          color: TEXT_DARK,
          mb: 1.5,
        }}
      >
        Why This Section Matters
      </Typography>
      <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.8, mb: 3 }}>
        {content.whyItMatters}
      </Typography>

      <Typography sx={{ color: TEXT_DARK, fontWeight: 500, mb: 2 }}>
        {content.actionPrompt}
      </Typography>

      {content.startWithTopicId && (
        <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} style={{ display: 'inline-block' }}>
          <Button
            variant="contained"
            endIcon={<ArrowRight size={18} />}
            onClick={() => onStartWithTopic(content.startWithTopicId!)}
            sx={{
              bgcolor: PRIMARY,
              color: '#fff',
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '12px',
              px: 2.5,
              py: 1.25,
              boxShadow: '0 2px 8px rgba(13, 148, 136, 0.3)',
              '&:hover': { bgcolor: PRIMARY_HOVER },
            }}
          >
            Start with: {content.startWithLabel} →
          </Button>
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        style={{ marginTop: 32 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.5,
            p: 2,
            borderRadius: '12px',
            bgcolor: PRIMARY_LIGHT,
            border: `1px solid ${BORDER}`,
          }}
        >
          <Lightbulb size={22} color={PRIMARY} style={{ flexShrink: 0, marginTop: 2 }} />
          <Box>
            <Typography sx={{ fontWeight: 600, color: TEXT_DARK, mb: 0.5, fontSize: '0.95rem' }}>
              Tip
            </Typography>
            <Typography sx={{ color: TEXT_MUTED, lineHeight: 1.7, fontSize: '0.9rem' }}>
              {content.tip}
            </Typography>
          </Box>
        </Box>
      </motion.div>

      <Box sx={{ mt: 3 }}>
        <Box sx={{ height: 8, bgcolor: BORDER, borderRadius: 4, overflow: 'hidden' }}>
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            style={{ height: '100%', backgroundColor: PRIMARY, borderRadius: 4 }}
          />
        </Box>
      </Box>
    </motion.div>
  );
}

const RoboticsLearningPath: React.FC = () => {
  const navigate = useNavigate();
  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedSectionId, setSelectedSectionId] = useState<string>('intro-robotics');
  const [expandedIds] = useState<Set<string>>(
    () => new Set(SIDEBAR_SECTIONS.map((s) => s.id))
  );
  const [progress] = useState(18);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  const selectSection = (id: string) => {
    setSelectedSectionId(id);
    setActiveId(null);
  };

  const closeDrawer = () => setDrawerOpen(false);
  const handleSectionClick = (id: string) => {
    selectSection(id);
    if (isMobile) closeDrawer();
  };
  const handleTopicClick = (topicId: string, sectionId: string) => {
    setActiveId(topicId);
    setSelectedSectionId(sectionId);
    if (isMobile) closeDrawer();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        minHeight: '100vh',
        pt: { xs: 2, md: 8 },
        pb: 2,
        bgcolor: BG,
        fontFamily: '"Inter", "Poppins", sans-serif',
      }}
    >
      <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5, borderBottom: `1px solid ${BORDER}`, bgcolor: CARD_BG, position: 'sticky', top: 0, zIndex: 11 }}>
        <Button startIcon={<ChevronLeft size={18} />} onClick={() => navigate('/')} size="small" sx={{ textTransform: 'none', fontWeight: 600, color: TEXT_DARK }}>Home</Button>
        <IconButton onClick={() => setDrawerOpen(true)} aria-label="Open menu" sx={{ color: TEXT_DARK }}><Menu size={24} /></IconButton>
      </Box>

      <Drawer anchor="left" open={drawerOpen} onClose={closeDrawer} sx={{ display: { xs: 'block', md: 'none' }, '& .MuiDrawer-paper': { width: 280, maxWidth: '85vw', pt: 2, pb: 2, px: 2, boxSizing: 'border-box' } }}>
        <Typography component="h2" sx={{ fontFamily: MENU_TITLE_FONT, fontSize: '1.2rem', fontWeight: 800, mb: 2, px: 1, color: TEXT_DARK }}>Robotics Learning Path</Typography>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {SIDEBAR_SECTIONS.map((section) => {
            const isExpanded = expandedIds.has(section.id);
            return (
              <li key={section.id} style={{ marginBottom: 6 }}>
                <Box onClick={() => handleSectionClick(section.id)} sx={{ py: 1.25, px: 2, cursor: 'pointer', borderRadius: '12px', display: 'flex', alignItems: 'center', gap: 1.25, fontFamily: MENU_SECTION_FONT, fontWeight: 700, fontSize: '0.95rem', color: isExpanded ? PRIMARY : TEXT_DARK, bgcolor: isExpanded ? PRIMARY_LIGHT : 'transparent', borderLeft: `3px solid ${isExpanded ? PRIMARY : 'transparent'}` }}>
                  {isExpanded ? <ChevronDown size={20} strokeWidth={2.5} color={PRIMARY} /> : <ChevronRight size={20} strokeWidth={2.5} color={TEXT_MUTED} />}
                  {section.label}
                </Box>
                {isExpanded && (
                  <Box sx={{ pl: 3.5, pr: 1, py: 0.75, borderLeft: `2px solid ${BORDER}`, ml: 1.5 }}>
                    {section.children.map((child) => (
                      <Box key={child.id} onClick={(e) => { e.stopPropagation(); handleTopicClick(child.id, section.id); }} sx={{ py: 1.1, px: 2, cursor: 'pointer', borderRadius: '10px', fontFamily: SUBMENU_FONT, fontSize: '0.925rem', fontWeight: activeId === child.id ? 700 : 500, bgcolor: activeId === child.id ? PRIMARY_LIGHT : 'transparent', color: activeId === child.id ? PRIMARY : '#475569', borderLeft: activeId === child.id ? `3px solid ${PRIMARY}` : '3px solid transparent' }}>
                        {child.label}
                      </Box>
                    ))}
                  </Box>
                )}
              </li>
            );
          })}
        </Box>
      </Drawer>

      <Box
        sx={{
          width: 280,
          flexShrink: 0,
          bgcolor: CARD_BG,
          borderRight: `1px solid ${BORDER}`,
          position: 'sticky',
          top: 56,
          height: 'calc(100vh - 56px)',
          overflowY: 'auto',
          py: 2.5,
          px: 2,
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          zIndex: 10,
          display: { xs: 'none', md: 'block' },
        }}
      >
        <Typography
          component="h2"
          sx={{
            fontFamily: MENU_TITLE_FONT,
            fontSize: '1.35rem',
            fontWeight: 800,
            letterSpacing: '-0.02em',
            background: `linear-gradient(135deg, ${TEXT_DARK} 0%, ${PRIMARY} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 2.5,
            px: 1,
            lineHeight: 1.2,
          }}
        >
          Robotics Learning Path
        </Typography>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
          {SIDEBAR_SECTIONS.map((section, sectionIndex) => {
            const isExpanded = expandedIds.has(section.id);
            return (
              <motion.li
                key={section.id}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.25, delay: sectionIndex * 0.04 }}
                style={{ marginBottom: 6 }}
              >
                <Box
                  onClick={() => handleSectionClick(section.id)}
                  sx={{
                    py: 1.25,
                    px: 2,
                    cursor: 'pointer',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    fontFamily: MENU_SECTION_FONT,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    letterSpacing: '0.01em',
                    color: isExpanded ? PRIMARY : TEXT_DARK,
                    bgcolor: isExpanded ? PRIMARY_LIGHT : 'transparent',
                    borderLeft: `3px solid ${isExpanded ? PRIMARY : 'transparent'}`,
                    transition: 'background 0.2s, color 0.2s, border-color 0.2s',
                    '&:hover': {
                      bgcolor: PRIMARY_LIGHT,
                      color: PRIMARY,
                    },
                  }}
                >
                  {isExpanded ? (
                    <ChevronDown size={20} strokeWidth={2.5} color={PRIMARY} />
                  ) : (
                    <ChevronRight size={20} strokeWidth={2.5} color={TEXT_MUTED} />
                  )}
                  {section.label}
                </Box>
                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25 }}
                      style={{ overflow: 'hidden' }}
                    >
                      <Box sx={{ pl: 3.5, pr: 1, py: 0.75, borderLeft: `2px solid ${BORDER}`, ml: 1.5 }}>
                        {section.children.map((child, i) => (
                          <motion.div
                            key={child.id}
                            initial={{ opacity: 0, x: -4 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.05 * i }}
                          >
                            <Box
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTopicClick(child.id, section.id);
                              }}
                              sx={{
                                py: 1.1,
                                px: 2,
                                cursor: 'pointer',
                                borderRadius: '10px',
                                fontFamily: SUBMENU_FONT,
                                fontSize: '0.925rem',
                                fontWeight: activeId === child.id ? 700 : 500,
                                letterSpacing: '0.015em',
                                bgcolor: activeId === child.id ? PRIMARY_LIGHT : 'transparent',
                                color: activeId === child.id ? PRIMARY : '#475569',
                                borderLeft: activeId === child.id ? `3px solid ${PRIMARY}` : '3px solid transparent',
                                transition: 'background 0.2s, color 0.2s',
                                '&:hover': {
                                  bgcolor: activeId === child.id ? PRIMARY_LIGHT : 'rgba(13, 148, 136, 0.08)',
                                  color: activeId === child.id ? PRIMARY_HOVER : TEXT_DARK,
                                },
                              }}
                            >
                              {child.label}
                            </Box>
                          </motion.div>
                        ))}
                      </Box>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.li>
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          py: { xs: 2, md: 4 },
          px: { xs: 2, sm: 4 },
          maxWidth: 900,
          width: '100%',
        }}
      >
        <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 2, mb: 3 }}>
          <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
            <Button
              startIcon={<ChevronLeft size={18} />}
              onClick={() => navigate('/')}
              sx={{
                bgcolor: PRIMARY,
                color: '#fff',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '12px',
                px: 2,
                py: 1,
                boxShadow: '0 2px 8px rgba(13, 148, 136, 0.3)',
                '&:hover': { bgcolor: PRIMARY_HOVER },
              }}
            >
              Home
            </Button>
          </motion.div>
        </Box>

        <SectionLandingView
          key={selectedSectionId}
          content={SECTION_LANDING[selectedSectionId] ?? SECTION_LANDING['intro-robotics']}
          onStartWithTopic={(topicId) => setActiveId(topicId)}
          progress={progress}
          activeId={activeId}
        />
      </Box>
    </Box>
  );
};

export default RoboticsLearningPath;
